import type { z } from 'zod';

/** Prefixo da API: em dev o Vite repassa ao backend; em produção, o ingress. */
const API_BASE = '/api';

/** O backend respondeu com erro (4xx/5xx). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * A resposta não bate com o contrato: frontend e backend estão em versões
 * diferentes. Falhar aqui, com o caminho do campo, é melhor que um
 * `undefined` aparecendo três telas depois.
 */
export class ContractMismatchError extends Error {
  constructor(path: string, issues: string) {
    super(`Resposta de ${path} fora do contrato: ${issues}`);
    this.name = 'ContractMismatchError';
  }
}

/** Devolve o token para a próxima chamada, ou `null` para chamar sem token. */
export type TokenGetter = () => Promise<string | null>;

export interface ApiClient {
  get<T>(path: string, schema: z.ZodType<T>): Promise<T>;
  post<T>(path: string, body: unknown, schema: z.ZodType<T>): Promise<T>;
  patch<T>(path: string, body: unknown, schema: z.ZodType<T>): Promise<T>;
  put<T>(path: string, body: unknown, schema: z.ZodType<T>): Promise<T>;
  /** Comando que responde 204, sem corpo. */
  postEmpty(path: string, body: unknown): Promise<void>;
  /** Um arquivo (o PDF da etiqueta, por exemplo), sem contrato JSON. */
  getBlob(path: string): Promise<Blob>;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'message' in body) {
      return String(body.message);
    }
  } catch {
    // Corpo vazio ou que não é JSON: fica a mensagem genérica.
  }
  return `Falha na requisição (${response.status})`;
}

export function createApiClient(getToken: TokenGetter): ApiClient {
  /** A chamada com o token; erro HTTP vira `ApiError`, com a mensagem da API. */
  async function send(
    method: string,
    path: string,
    accept: string,
    body?: unknown,
  ): Promise<Response> {
    const token = await getToken();
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        accept,
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(token === null ? {} : { authorization: `Bearer ${token}` }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response.ok) {
      throw new ApiError(response.status, await errorMessage(response));
    }
    return response;
  }

  async function request<T>(
    method: string,
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
  ): Promise<T> {
    const response = await send(method, path, 'application/json', body);
    const parsed = schema.safeParse(await response.json());
    if (!parsed.success) {
      throw new ContractMismatchError(path, parsed.error.message);
    }
    return parsed.data;
  }

  return {
    get: (path, schema) => request('GET', path, schema),
    post: (path, body, schema) => request('POST', path, schema, body),
    patch: (path, body, schema) => request('PATCH', path, schema, body),
    put: (path, body, schema) => request('PUT', path, schema, body),
    postEmpty: async (path, body) => {
      await send('POST', path, 'application/json', body);
    },
    getBlob: async (path) => (await send('GET', path, '*/*')).blob(),
  };
}

/** Para as rotas públicas, chamadas antes de existir token. */
export const publicApi = createApiClient(() => Promise.resolve(null));
